import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import apiClient from '../../../services/apiService';
import { useToast } from '../../../components/ui/Toast';
import { INVITE_STATE, getInviteState, inviteDaysLeft, inviteExpiredAgo } from '../utils/inviteState';

const RequestDetailsModal = ({ request, isAdmin, isCoordinator, onClose, onApprove, onReject, onInviteVendor, onContinueDraft, getStatusColor }) => {
  const toast = useToast();
  // Real invite state (expiry-aware) rather than the sticky vendor_invited flag.
  const inviteState = getInviteState(request);
  const daysLeft = inviteDaysLeft(request);
  const expiredAgo = inviteExpiredAgo(request);
  const handleDownloadDocument = async () => {
    try {
      const response = await apiClient.get(`/supplier-requests/${request.id}/approval-document`, {
        responseType: 'blob',
      });
      
      // Extract filename from content-disposition header
      // Axios normalizes headers to lowercase, and they can be a string or array
      const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
      let filename = `approval-document-${request.id}.pdf`;
      
      if (contentDisposition) {
        // Handle both string and array formats
        const headerValue = Array.isArray(contentDisposition) ? contentDisposition[0] : contentDisposition;
        // Match filename=value (handles both quoted and unquoted)
        // Pattern: filename="value" or filename=value
        const quotedMatch = headerValue.match(/filename[^=]*=\s*"([^"]+)"/i);
        const unquotedMatch = headerValue.match(/filename[^=]*=\s*([^;]+)/i);
        
        if (quotedMatch && quotedMatch[1]) {
          filename = quotedMatch[1].trim();
        } else if (unquotedMatch && unquotedMatch[1]) {
          filename = unquotedMatch[1].trim();
        }
      } else if (request.plant_head_approval_document) {
        // Fallback: Extract filename from stored path
        // Format is: filePath|iv:authTag|checksum
        const filePath = request.plant_head_approval_document.split('|')[0];
        const extractedName = filePath.split(/[\\/]/).pop();
        if (extractedName) {
          // Preserve the original extension
          filename = extractedName;
        }
      }
      
      // Ensure filename has an extension
      if (!filename.includes('.')) {
        filename = `${filename}.pdf`;
      }
      
      // Create a download link and trigger it
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl border border-slate-200 max-w-4xl w-full max-h-[calc(90vh/var(--app-scale))] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-slate-50 border-b border-slate-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <h2 className="text-2xl font-bold text-slate-900">Request Details</h2>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-full p-2 transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 min-h-0">
          {/* Status Badge */}
          <div className="mb-6">
            <span className={`px-4 py-2 rounded-full text-sm font-semibold border ${getStatusColor(request.status)}`}>
              {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
            </span>
          </div>

          {/* Draft Notice */}
          {request.status === 'draft' && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold text-blue-900">Draft Request</p>
                  <p className="text-sm text-blue-700 mt-1">
                    Continue editing this draft and submit it for admin approval when ready.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Request Information */}
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">User Name</label>
              <p className="text-slate-900">{request.user_name}</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">User Site & Plant</label>
              <p className="text-slate-900">{request.user_site_plant}</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Required Vendor Name</label>
              <p className="text-slate-900 font-medium">{request.required_vendor_name}</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Vendor Contact</label>
              <p className="text-slate-900">{request.vendor_contact}</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Vendor Email</label>
              <p className="text-slate-900">{request.vendor_email}</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Requested By</label>
              <p className="text-slate-900">{request.requested_by}</p>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-semibold text-slate-700 mb-1">Reason for Creating New Vendor Code</label>
            <p className="text-slate-900 bg-slate-50 p-4 rounded-lg border border-slate-200">{request.reason_for_new_vendor}</p>
          </div>

          {/* Submitted by */}
          {request.user && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <label className="block text-sm font-semibold text-blue-900 mb-2">Submitted By</label>
              <p className="text-blue-800">
                {request.user.full_name} ({request.user.email})
              </p>
            </div>
          )}

          {/* Plant Head Approval Document */}
          {request.plant_head_approval_document && (
            <div className="mb-6">
              <label className="block text-sm font-semibold text-slate-700 mb-3">Plant Head Approval Document</label>
              <div className="border-2 border-slate-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Icon name="FileText" size={48} className="text-blue-600" />
                    <div>
                      <p className="font-medium text-slate-900">Plant Head Approval Document</p>
                      <p className="text-sm text-slate-600">
                        {request.plant_head_approval_document?.includes('.pdf') ? 'PDF Document' : 'Image Document'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          const response = await apiClient.get(`/supplier-requests/${request.id}/approval-document`, {
                            responseType: 'blob',
                          });
                          const url = window.URL.createObjectURL(response.data);
                          const win = window.open(url, '_blank');
                          setTimeout(() => window.URL.revokeObjectURL(url), 1500);
                          if (!win) {
                            window.location.href = url; // fallback in case popup is blocked
                          }
                        } catch (e) {
                          console.error('Error previewing document:', e);
                          toast.error('Preview failed. Please try again.');
                        }
                      }}
                      iconName="Eye"
                      iconPosition="left"
                    >
                      Preview
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={handleDownloadDocument}
                      iconName="Download"
                      iconPosition="left"
                    >
                      Download
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Approval/Rejection Info */}
          {request.status?.toLowerCase() === 'approved' && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="CheckCircle" size={20} className="text-green-600" />
                <span className="font-semibold text-green-900">Approved</span>
              </div>
              {request.approved_at && (
                <p className="text-sm text-green-800">
                  Approved on: {new Date(request.approved_at).toLocaleString()}
                </p>
              )}
              {request.approver && (
                <p className="text-sm text-green-800">
                  By: {request.approver.full_name}
                </p>
              )}
              
              {/* Registration Link */}
              {request.invite_url && (
                <div className="mt-4 pt-4 border-t border-green-200">
                  <label className="block text-sm font-semibold text-green-900 mb-2">
                    Vendor Registration Link
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={request.invite_url}
                      className="flex-1 px-3 py-2 bg-white border border-green-300 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(request.invite_url);
                        toast.success('Registration link copied to clipboard!');
                      }}
                      iconName="Copy"
                      iconPosition="left"
                      className="border-green-300 text-green-700 hover:bg-green-100"
                    >
                      Copy
                    </Button>
                  </div>
                  {request.invite?.expires_at && (
                    <p className="text-xs text-green-700 mt-2">
                      Expires on: {new Date(request.invite.expires_at).toLocaleString()}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {request.status?.toLowerCase() === 'rejected' && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="XCircle" size={20} className="text-red-600" />
                <span className="font-semibold text-red-900">Rejected</span>
              </div>
              {request.rejection_reason && (
                <p className="text-sm text-red-800 mb-2">
                  Reason: {request.rejection_reason}
                </p>
              )}
              {request.approved_at && (
                <p className="text-sm text-red-800">
                  Rejected on: {new Date(request.approved_at).toLocaleString()}
                </p>
              )}
              {request.approver && (
                <p className="text-sm text-red-800">
                  By: {request.approver.full_name}
                </p>
              )}
            </div>
          )}

          {/* Vendor Invitation Status */}
          {request.status?.toLowerCase() === 'approved' && (
            <div className="mb-6">
              {inviteState === INVITE_STATE.EXPIRED ? (
                // Previously this branch rendered the same green "Vendor Invited"
                // panel forever. The link is gone, so say so and offer the fix.
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="Clock" size={20} className="text-red-600" />
                    <span className="font-semibold text-red-900">
                      Invitation link expired
                    </span>
                  </div>
                  <p className="text-sm text-red-800">
                    The registration link sent to the vendor is no longer valid
                    {expiredAgo ? ` (${expiredAgo})` : ''}. The vendor cannot register
                    until a new invitation is sent.
                  </p>
                  {request.vendor_invited_at && (
                    <p className="text-sm text-red-800 mt-1">
                      Originally invited on: {new Date(request.vendor_invited_at).toLocaleString()}
                    </p>
                  )}
                  {isCoordinator && (
                    <Button
                      onClick={() => onInviteVendor(request)}
                      className="mt-3 bg-blue-600 hover:bg-blue-700 text-white transition-all duration-200 ease-in-out hover:shadow-md active:scale-[0.98]"
                    >
                      <Icon name="RefreshCw" size={18} />
                      Send a new invitation
                    </Button>
                  )}
                </div>
              ) : inviteState === INVITE_STATE.REGISTERED ? (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="UserCheck" size={20} className="text-green-600" />
                    <span className="font-semibold text-green-900">Vendor Registered</span>
                  </div>
                  <p className="text-sm text-green-800">
                    The vendor used the invitation and submitted their registration.
                  </p>
                </div>
              ) : inviteState === INVITE_STATE.INVITED ? (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="Mail" size={20} className="text-green-600" />
                    <span className="font-semibold text-green-900">Vendor Invited</span>
                  </div>
                  {request.vendor_invited_at && (
                    <p className="text-sm text-green-800">
                      Invited on: {new Date(request.vendor_invited_at).toLocaleString()}
                    </p>
                  )}
                  {daysLeft != null && (
                    <p className="text-sm text-green-800">
                      Link valid for {daysLeft} more day{daysLeft === 1 ? '' : 's'}.
                    </p>
                  )}
                  {request.inviter && (
                    <p className="text-sm text-green-800">
                      By: {request.inviter.full_name}
                    </p>
                  )}
                </div>
              ) : (
                <>
                  {/* Coordinator - Can Invite */}
                  {isCoordinator && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800 mb-3">
                        <Icon name="Info" size={16} className="inline mr-2" />
                        This request has been approved. You can now invite the vendor.
                      </p>
                      <Button
                        onClick={() => onInviteVendor(request)}
                        className="bg-blue-600 hover:bg-blue-700 text-white transition-all duration-200 ease-in-out hover:shadow-md active:scale-[0.98]"
                      >
                        <Icon name="Mail" size={18} />
                        Invite Vendor Now
                      </Button>
                    </div>
                  )}
                  
                  {/* Admin - Waiting for Coordinator */}
                  {isAdmin && !isCoordinator && (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        <Icon name="Clock" size={16} className="inline mr-2" />
                        Waiting for coordinator to invite vendor
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Timestamps */}
          <div className="mt-6 pt-6 border-t border-slate-200 text-sm text-slate-600">
            <p>Created: {new Date(request.created_at).toLocaleString()}</p>
            {request.updated_at && (
              <p>Last Updated: {new Date(request.updated_at).toLocaleString()}</p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="bg-slate-50 px-6 py-4 flex justify-end gap-3 border-t border-slate-200 flex-shrink-0">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          
          {/* Draft: Continue Button */}
          {request.status === 'draft' && onContinueDraft && (
            <Button
              variant="default"
              onClick={() => {
                onClose();
                onContinueDraft(request);
              }}
              iconName="Edit"
              iconPosition="left"
            >
              Continue
            </Button>
          )}

          {/* Submitted: Admin/Coordinator Approval Buttons */}
          {(isAdmin || isCoordinator) && request.status?.toLowerCase() === 'submitted' && (
            <>
              <Button
                variant="success"
                onClick={() => {
                  onClose();
                  onApprove(request);
                }}
                iconName="CheckCircle"
                iconPosition="left"
              >
                Approve
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  onClose();
                  onReject(request);
                }}
                iconName="XCircle"
                iconPosition="left"
              >
                Reject
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestDetailsModal;

